import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  leaderboardEvents,
  ParticipantsResponse,
  LeaderboardParticipant,
} from '../api/leaderboard-events';
import { useAlert } from '../contexts/AlertContext';

export const useLeaderboardParticipants = (
  eventId?: number,
  userName?: string,
  dateFrom?: Date | null,
  dateTo?: Date | null
) => {
  const [participants, setParticipants] = useState<LeaderboardParticipant[]>([]);
  const [myParticipant, setMyParticipant] = useState<LeaderboardParticipant | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const { showAlert } = useAlert();

  const offset = useMemo(() => (pageNumber - 1) * pageSize, [pageNumber, pageSize]);

  const fetchParticipants = async () => {
    if (!eventId || eventId === undefined) return;

    try {
      setIsLoading(true);
      const response: ParticipantsResponse = await leaderboardEvents.getParticipants(eventId, {
        limit: pageSize,
        offset,
        userName: userName?.trim() || undefined,
        dateFrom: dateFrom
          ? format(
              new Date(dateFrom.getFullYear(), dateFrom.getMonth(), dateFrom.getDate(), 0, 0, 0),
              "yyyy-MM-dd'T'HH:mm:ss"
            )
          : undefined,
        dateTo: dateTo
          ? format(
              new Date(dateTo.getFullYear(), dateTo.getMonth(), dateTo.getDate(), 23, 59, 59),
              "yyyy-MM-dd'T'HH:mm:ss"
            )
          : undefined,
      });
      setParticipants(response.participants || []);
      setMyParticipant(response.myParticipant || null);
      setTotalCount(response.totalCount || 0);
      setPageSize(response.pageSize || pageSize);
    } catch (error) {
      console.error('Failed to fetch leaderboard participants:', error);
      showAlert('Failed to fetch leaderboard participants', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchParticipants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, pageSize, offset, userName, dateFrom?.getTime(), dateTo?.getTime()]);

  const totalPages = useMemo(() => {
    return pageSize ? Math.ceil(totalCount / pageSize) : 0;
  }, [totalCount, pageSize]);

  return {
    participants,
    myParticipant,
    isLoading,
    pageNumber,
    pageSize,
    totalCount,
    totalPages,
    setPageNumber,
    setPageSize,
    refetch: fetchParticipants,
  };
};
